import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Spinner, 
  Alert, 
  Container, 
  Button, 
  Badge,
  Row,
  Col,
  Breadcrumb,
  Modal
} from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaEye } from 'react-icons/fa';
import { getEmployees } from '../../../services/employeeService';
import { Form, InputGroup, FormControl, FormSelect } from 'react-bootstrap';
import { FaSearch, FaFilter } from 'react-icons/fa';
import { getEmployeeAppraisals } from '../services/appraisalService';
import AppraisalForm from './AppraisalForm';
import moment from 'moment';
import TopBar from '../../../components/TopBar';

const EmployeeAppraisalList = () => {
  const [employees, setEmployees] = useState(null); // Initialize as null to track initial load
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    position: ''
  });
  const [departments, setDepartments] = useState([]); // Will store {id, name} objects
  const [positions, setPositions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    in_progress: 0,
    average_rating: 0
  });
  const [showAppraisalForm, setShowAppraisalForm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle new appraisal button click
  const handleNewAppraisal = (employee) => {
    setSelectedEmployee(employee);
    setShowAppraisalForm(true);
    
    // Scroll to top when opening the modal
    window.scrollTo(0, 0);
  };
  
  // Close the appraisal form
  const handleCloseAppraisalForm = () => {
    setShowAppraisalForm(false);
  };
  
  // Fetch appraisals for the selected employee
  const fetchAppraisals = async (employeeId) => {
    try {
      setLoading(true);
      const data = await getEmployeeAppraisals(employeeId);
      setAppraisals(data);
    } catch (err) {
      console.error('Error fetching appraisals:', err);
      setError('Failed to load appraisals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle successful appraisal submission
  const handleAppraisalSuccess = () => {
    setShowAppraisalForm(false);
    // Refresh the appraisals list
    if (selectedEmployee) {
      fetchAppraisals(selectedEmployee.id);
    }
  };

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearchTerm
    }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        
        // Log the filters for debugging
        console.log('Fetching employees with filters:', {
          search: filters.search,
          department: filters.department,
          position: filters.position
        });
        
        // Pass department ID to the API
        const response = await getEmployees(
          filters.search,
          filters.department || ''
        );
        
        console.log('API Response:', response);
        
        // Filter by position on the client side if needed
        let filteredData = response.data || [];
        if (filters.position) {
          filteredData = filteredData.filter(emp => 
            emp.position && emp.position.toLowerCase().includes(filters.position.toLowerCase())
          );
        }
        
        setEmployees(filteredData);
        
        // Extract unique departments and positions for filters
        if (response.data && response.data.length > 0) {
          const deptMap = new Map();
          const positionSet = new Set();
          
          response.data.forEach(emp => {
            if (emp.department?.id && emp.department?.name) {
              deptMap.set(emp.department.id, emp.department.name);
            }
            if (emp.position) positionSet.add(emp.position);
          });
          
          // Convert map to array of {id, name} objects and sort by name
          const departmentsArray = Array.from(deptMap.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
            
          setDepartments(departmentsArray);
          setPositions(Array.from(positionSet).sort());
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch employees. Please try again later.');
        console.error('Error fetching employees:', err);
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [filters]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const resetFilters = () => {
    setFilters({
      search: '',
      department: '',
      position: ''
    });
  };

  const calculateStats = (appraisals) => {
    const total = appraisals.length;
    const completed = appraisals.filter(a => a.status?.toLowerCase() === 'completed').length;
    const in_progress = appraisals.filter(a => a.status?.toLowerCase() === 'in_progress' || a.status?.toLowerCase() === 'in-progress').length;
    const ratings = appraisals.filter(a => a.overall_rating).map(a => parseFloat(a.overall_rating));
    const average_rating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length) : 0;
    
    return {
      total,
      completed,
      in_progress,
      average_rating
    };
  };

  const handleEmployeeSelect = async (employee) => {
    try {
      setLoading(true);
      setSelectedEmployee(employee);
      const response = await getEmployeeAppraisals(employee.id);
      console.log('Employee appraisals response:', response);
      // Ensure appraisals is always an array, even if response.data is undefined
      const appraisalsData = Array.isArray(response) ? response : [];
      setAppraisals(appraisalsData);
      setStats(calculateStats(appraisalsData));
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch appraisals for ${employee?.name || 'the employee'}. Please try again.`);
      console.error('Error fetching employee appraisals:', err);
      setLoading(false);
      // Reset appraisals on error
      setAppraisals([]);
    }
  };

  const handleBackToEmployees = () => {
    setSelectedEmployee(null);
    setAppraisals([]);
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'warning';
      case 'pending':
        return 'info';
      case 'overdue':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Show loading spinner only during initial load or when fetching employee appraisals
  if (loading && !employees) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <TopBar />
    <Container className="py-4">
    
       {selectedEmployee &&
             <div className="d-flex align-items-center mb-4">
              <Button 
                variant="outline-dark" 
                onClick={handleBackToEmployees}
                className="d-flex align-items-center px-3 py-2 rounded-3"
                style={{
                  transition: 'all 0.2s ease',
                  border: '1px solid #212529',
                  color: '#212529',
                  backgroundColor: 'white',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#212529';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateX(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = '#212529';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <FaArrowLeft className="me-2" style={{ fontSize: '0.9rem' }} />
                <span style={{ fontSize: '0.9rem' }}>Back to Employees</span>
              </Button>
            </div>
       }
      {/* Summary Card */}
    
     

      {!selectedEmployee ? (
        <>
          <Card className="mb-4">
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Filters</h5>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4} className="mb-3">
              <Form.Group>
                <Form.Label>Search by Name/ID</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <FormControl
                    type="text"
                    placeholder="Search by name or ID"
                    name="search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{ height: '32px' }}
                    className="py-0"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Department</Form.Label>
                <FormSelect
                  name="department"
                  value={filters.department}
                  onChange={handleFilterChange}
                  style={{ height: '32px', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                  className="py-0"
                >
                  <option value="">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </FormSelect>
              </Form.Group>
            </Col>
            {/* <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Position</Form.Label>
                <FormSelect
                  name="position"
                  value={filters.position}
                  onChange={handleFilterChange}
                  style={{ height: '32px', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                  className="py-0"
                >
                  <option value="">All Positions</option>
                  {positions.map((pos, index) => (
                    <option key={index} value={pos}>
                      {pos}
                    </option>
                  ))}
                </FormSelect>
              </Form.Group>
            </Col> */}
            <Col md={2} className="d-flex align-items-end mb-3">
              <Button 
                variant="outline-secondary" 
                onClick={resetFilters}
                className="w-100"
                disabled={!filters.search && !filters.department && !filters.position}
              >
                Reset Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          <Table hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee ID</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Position</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employees === null ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading employees...
                      </td>
                    </tr>
                  ) : employees && employees.length > 0 ? (
                    employees.map((employee, index) => (
                      <tr key={employee.id}>
                        <td>{index + 1}</td>
                        <td>{employee.emp_code || 'N/A'}</td>
                        <td>{employee.name || 'N/A'}</td>
                        <td>{employee.department?.name || 'N/A'}</td>
                        <td>{employee.employee_type || 'N/A'}</td>
                        <td>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => handleEmployeeSelect(employee)}
                          >
                            View Appraisals
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">No employees found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      ) : (
        <>
   
   <Card className="mb-4">
   <Card.Body>   
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">Appraisals for {selectedEmployee.name}</h2>
              <p className="text-muted mb-0">
                {selectedEmployee.department?.name || 'N/A'} • {selectedEmployee.employee_type || 'N/A'}
              </p>
            </div>
            <Button 
              variant="primary"
              onClick={() => handleNewAppraisal(selectedEmployee)}
            >
              New Appraisal for {selectedEmployee.name.split(' ')[0]}
            </Button>
          </div>
          </Card.Body>
          </Card>

          {/* Stats Cards */}
          {selectedEmployee && (
            <Row className="mb-4">
              <Col md={3} sm={6} className="mb-3 mb-md-0">
                <Card className="border-0 bg-soft-primary">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-muted mb-0">Total</h6>
                        <h3 className="mb-0">{stats.total || 0}</h3>
                      </div>
                      <div className="icon-shape icon-lg bg-primary-soft text-primary rounded-3">
                        <i className="bi bi-clipboard-data fs-4"></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-3 mb-md-0">
                <Card className="border-0 bg-soft-success">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-muted mb-0">Completed</h6>
                        <h3 className="mb-0">{stats.completed || 0}</h3>
                      </div>
                      <div className="icon-shape icon-lg bg-success-soft text-success rounded-3">
                        <i className="bi bi-check-circle fs-4"></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6} className="mb-3 mb-md-0">
                <Card className="border-0 bg-soft-info">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-muted mb-0">In Progress</h6>
                        <h3 className="mb-0">{stats.in_progress || 0}</h3>
                      </div>
                      <div className="icon-shape icon-lg bg-info-soft text-info rounded-3">
                        <i className="bi bi-arrow-repeat fs-4"></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} sm={6}>
                <Card className="border-0 bg-soft-warning">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-uppercase text-muted mb-0">Avg. Rating</h6>
                        <h3 className="mb-0">
                          {stats.average_rating ? stats.average_rating.toFixed(1) : 'N/A'}
                        </h3>
                      </div>
                      <div className="icon-shape icon-lg bg-warning-soft text-warning rounded-3">
                        <i className="bi bi-star-fill fs-4"></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Card>
            <Card.Body>
              {appraisals.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Appraisal Period</th>
                        <th>Status</th>
                        <th>Overall Rating</th>
                        <th>Last Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appraisals.map((appraisal, index) => (
                        <tr key={appraisal.id}>
                          <td>{index + 1}</td>
                          <td>
                            {/* {appraisal.appraisal_period?.name || 'N/A'} */}
                            <div className="text-muted small">
                              {moment(appraisal.start_date).format('MMM D, YYYY')} - {moment(appraisal.end_date).format('MMM D, YYYY')}
                            </div>
                          </td>
                          <td>
                            <Badge bg={getStatusBadge(appraisal.status)}>
                              {appraisal.status || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            {appraisal.overall_rating ? `${appraisal.overall_rating}/5` : 'N/A'}
                          </td>
                          <td>
                            {appraisal.updated_at 
                              ? moment(appraisal.updated_at).format('MMM D, YYYY')
                              : 'N/A'}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              as={Link}
                              to={`/app/appraisals/${appraisal.id}`}
                            >
                              <FaEye className="me-1" /> View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info">
                  No appraisals found for this employee.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </>
      )}
      
      {/* Full Page Appraisal Form Modal */}
      <Modal 
        show={showAppraisalForm && selectedEmployee} 
        onHide={handleCloseAppraisalForm}
        size="xl"
        fullscreen
        centered
        className="appraisal-form-modal"
      >
        <Modal.Header closeButton className="border-0">
          {/* <Modal.Title>
            New Appraisal for {selectedEmployee?.name}
          </Modal.Title> */}
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="appraisal-form-container">
            {selectedEmployee && (
              <AppraisalForm 
                employee={selectedEmployee}
                onSuccess={handleAppraisalSuccess}
                onCancel={handleCloseAppraisalForm}
                refetchAppraisals={() => selectedEmployee && fetchAppraisals(selectedEmployee.id)}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
      
      <style jsx global>{`
        .appraisal-form-modal .modal-content {
          height: 100vh;
          border: none;
          border-radius: 0;
        }
        .appraisal-form-modal .modal-body {
          height: calc(100vh - 56px);
          overflow-y: auto;
          padding: 1.5rem;
        }
        
        .appraisal-form-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }
        
        .appraisal-form-container .card {
          border: none;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }
        
        .appraisal-form-container .form-label {
          font-weight: 500;
          margin-bottom: 0.5rem;
        }
        
        .appraisal-form-container .form-control,
        .appraisal-form-container .form-select {
          padding: 0.5rem 0.75rem;
          border-radius: 0.375rem;
        }
        .appraisal-form-modal .modal-header {
          position: sticky;
          top: 0;
          background: white;
          z-index: 1050;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          padding: 1rem 1.5rem;
        }
        .appraisal-form-modal .modal-title {
          font-size: 1.25rem;
          font-weight: 600;
        }
      `}</style>
    </Container>
    </>
  );
};

export default EmployeeAppraisalList;
